import { useCallback, useEffect, useRef } from 'react';
import {
    computeMultiSelectRangeIds,
    findMultiSelectItemIndex,
    isMultiSelectSelectableStatus,
} from '@src/util/FJMultiSelectRangeUtil';

type ListItem = { id: string | number; status?: string };

type UseShiftRangeMultiSelectOptions<T extends ListItem> = {
    items: T[];
    selectedIds: string[];
    onSetSelection: (ids: string[]) => void;
    onToggleOne: (id: string) => void;
    /** 是否允许勾选；默认与详情页一致（排除 processing） */
    isSelectableStatus?: (status?: string) => boolean;
};

/**
 * 多选模式下普通点击切换单项并更新锚点；Shift+点击在锚点与当前项之间连选（替换为区间选中集）。
 */
export function useShiftRangeMultiSelect<T extends ListItem>({
    items,
    selectedIds,
    onSetSelection,
    onToggleOne,
    isSelectableStatus = isMultiSelectSelectableStatus,
}: UseShiftRangeMultiSelectOptions<T>) {
    const anchorIndexRef = useRef<number | null>(null);

    useEffect(() => {
        if (selectedIds.length === 0) {
            anchorIndexRef.current = null;
        }
    }, [selectedIds.length]);

    const handleItemSelectClick = useCallback(
        (id: string | number, shiftKey = false) => {
            const index = findMultiSelectItemIndex(items, id);
            if (index < 0) {
                return;
            }
            const item = items[index];
            if (!isSelectableStatus(item.status)) {
                return;
            }

            if (shiftKey) {
                let anchor = anchorIndexRef.current;
                if (anchor == null || anchor < 0) {
                    if (selectedIds.length > 0) {
                        anchor = findMultiSelectItemIndex(items, selectedIds[0]);
                    }
                    if (anchor == null || anchor < 0) {
                        anchor = index;
                    }
                }
                onSetSelection(computeMultiSelectRangeIds(items, anchor, index, isSelectableStatus));
                return;
            }

            onToggleOne(String(id));
            anchorIndexRef.current = index;
        },
        [items, selectedIds, onSetSelection, onToggleOne, isSelectableStatus],
    );

    return { handleItemSelectClick };
}
